/**
 * CEO-only: overturn an approval that has already been granted.
 *
 * The reason is mandatory (the server rejects an empty one with 400, and the
 * button stays disabled until something is typed). Two people are notified —
 * the employee, whose card goes back for correction, and whoever originally
 * approved it, whose decision has just been overruled — so the modal says so
 * out loud before the click.
 *
 * When the month's payouts have already been actualised the server answers 409
 * with `meta.requires_payroll_confirmation`; the modal then surfaces the server's
 * own wording and requires an explicit tick before repeating the call.
 */
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AlertTriangle, Undo2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { useRevokeApproval } from "../hooks/useTimesheets";
import type { Timesheet } from "../types";

const schema = z.object({
  comment: z.string().trim().min(1, "Powód cofnięcia zatwierdzenia jest wymagany."),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  /** The approved card being un-approved; null closes the modal. */
  timesheet: Timesheet | null;
  onClose: () => void;
}

/** Pull the server's message out of the { data, errors, meta } envelope. */
function readServerError(err: unknown): { detail: string; needsPayrollConfirm: boolean } {
  const response = (
    err as {
      response?: {
        status?: number;
        data?: {
          errors?: { detail?: string; comment?: string[] };
          meta?: { requires_payroll_confirmation?: boolean };
        };
      };
    }
  )?.response;
  const errors = response?.data?.errors;
  return {
    detail:
      errors?.detail ??
      errors?.comment?.[0] ??
      "Nie udało się cofnąć zatwierdzenia. Spróbuj ponownie.",
    needsPayrollConfirm:
      response?.status === 409 || !!response?.data?.meta?.requires_payroll_confirmation,
  };
}

export function RevokeApprovalModal({ timesheet, onClose }: Props) {
  const revokeApproval = useRevokeApproval();
  const [serverError, setServerError] = useState("");
  const [payrollWarning, setPayrollWarning] = useState("");
  const [payrollConfirmed, setPayrollConfirmed] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { comment: "" },
  });

  // Reopening on another card must not carry over the previous reason or warning.
  useEffect(() => {
    if (timesheet) {
      reset({ comment: "" });
      setServerError("");
      setPayrollWarning("");
      setPayrollConfirmed(false);
    }
  }, [timesheet, reset]);

  const comment = watch("comment") ?? "";
  const blocked = !comment.trim() || (!!payrollWarning && !payrollConfirmed);

  const onSubmit = (values: FormValues) => {
    if (!timesheet) return;
    setServerError("");
    revokeApproval.mutate(
      {
        id: timesheet.id,
        comment: values.comment.trim(),
        confirmPayrollSettled: payrollConfirmed,
      },
      {
        onSuccess: () => onClose(),
        onError: (err: unknown) => {
          const { detail, needsPayrollConfirm } = readServerError(err);
          if (needsPayrollConfirm) {
            setPayrollWarning(detail);
            setPayrollConfirmed(false);
          } else {
            setServerError(detail);
          }
        },
      },
    );
  };

  return (
    <Modal
      open={!!timesheet}
      onClose={onClose}
      title="Cofnij zatwierdzenie karty godzin"
    >
      <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
        {timesheet && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm">
            <p className="font-medium text-red-800 dark:text-red-300">
              {timesheet.employee_name}
            </p>
            <p className="text-red-700 dark:text-red-400 text-xs mt-0.5">
              {timesheet.period_display}
            </p>
            {timesheet.reviewed_by_name && (
              <p className="text-red-700 dark:text-red-400 text-xs mt-1">
                Zatwierdził: {timesheet.reviewed_by_name}
              </p>
            )}
          </div>
        )}

        <div className="flex gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
          <AlertTriangle
            size={16}
            className="mt-0.5 flex-shrink-0 text-amber-600 dark:text-amber-400"
          />
          <p className="text-xs text-amber-800 dark:text-amber-300">
            Karta wróci do statusu <span className="font-medium">odrzucona</span> i pracownik
            będzie mógł złożyć poprawioną. Powiadomienie dostaną{" "}
            <span className="font-medium">dwie osoby</span>: pracownik oraz osoba, która
            kartę zatwierdziła — jej decyzja zostaje uchylona. Pierwotne zatwierdzenie
            zostaje w historii karty.
          </p>
        </div>

        <div>
          <label
            htmlFor="revoke-approval-reason"
            className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1"
          >
            Powód cofnięcia <span className="text-red-500">*</span>
          </label>
          <textarea
            id="revoke-approval-reason"
            rows={3}
            className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
            placeholder="Uzasadnienie cofnięcia zatwierdzenia karty godzin..."
            {...register("comment")}
          />
          {errors.comment && (
            <p className="text-xs text-red-500 mt-1">{errors.comment.message}</p>
          )}
        </div>

        {payrollWarning && (
          <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 p-3">
            <p className="text-xs text-red-700 dark:text-red-300">{payrollWarning}</p>
            <label className="mt-2 flex items-start gap-2 text-xs text-red-800 dark:text-red-300">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={payrollConfirmed}
                onChange={(e) => setPayrollConfirmed(e.target.checked)}
              />
              <span>
                Wiem, że wypłaty za ten miesiąc są już rozliczone — cofam zatwierdzenie mimo to.
              </span>
            </label>
          </div>
        )}

        {serverError && <p className="text-sm text-red-600">{serverError}</p>}

        <div className="flex gap-3">
          <Button
            type="submit"
            variant="secondary"
            loading={revokeApproval.isPending}
            disabled={blocked}
          >
            <Undo2 size={14} className="mr-1.5" /> Cofnij zatwierdzenie
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            <XCircle size={14} className="mr-1.5" /> Anuluj
          </Button>
        </div>
      </form>
    </Modal>
  );
}
